import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useApi } from '../../hooks/useApi';
import StatusPill from '../../components/StatusPill';
import Select from '../../components/ui/Select';
import { CodeChip } from '../../components/ui/CountrySelect';
import { Card, EmptyState, LoadingRows, Notice, PageHeader } from '../../components/ui/Page';
import { countryName } from '../../lib/countries';
import { date, money, moneyMajor } from '../../lib/format';

export type Schedule = {
  id: string;
  countryCode: string;
  countryName: string;
  currency: string;
  ratingFee: number;
  successFeePercent: number;
  minSuccessFee: number;
  taxName: string;
  taxPercent: number;
  taxInclusive: boolean;
  taxNumber: string | null;
  serviceCode: string | null;
  taxNote: string | null;
  isActive: boolean;
  notes: string | null;
  updatedAt: string;
  updatedBy: { name: string | null; email: string } | null;
};

export type TaxDefault = { taxName: string; taxPercent: number; taxNote?: string };

export type FeesResponse = {
  gateway: string;
  currencies: string[];
  taxDefaults: Record<string, TaxDefault>;
  schedules: Schedule[];
  companyCountries: { countryCode: string; companies: number }[];
};

export const DEFAULT_CODE = '*';
export const scheduleSlug = (code: string) => (code === DEFAULT_CODE ? 'default' : code);

export function taxLabel(s: Pick<Schedule, 'taxName' | 'taxPercent' | 'taxInclusive'>): string {
  if (!s.taxPercent) return `${s.taxName} 0%`;
  return `${s.taxName} ${s.taxPercent}% ${s.taxInclusive ? 'incl.' : '+ on top'}`;
}

/** No rate and no statutory note means nobody has decided how this country is taxed. */
const taxMissing = (s: Schedule) => !s.taxPercent && !s.taxNote;

const GATEWAY: Record<string, { label: string; tone: 'green' | 'amber' | 'rose'; hint: string }> = {
  razorpay: { label: 'Razorpay live', tone: 'green', hint: 'India pays with UPI, cards, netbanking and wallets; other countries with international cards.' },
  sandbox: { label: 'Sandbox', tone: 'amber', hint: 'No Razorpay keys — payments are simulated in development.' },
  disabled: { label: 'Disabled', tone: 'rose', hint: 'No Razorpay keys in production — record bank transfers under Payments.' },
};

/** Example raises per currency for the earnings calculator (major units). */
const RAISE_STEPS: Record<string, number[]> = {
  INR: [2500000, 5000000, 10000000, 20000000, 50000000, 100000000],
  USD: [100000, 250000, 500000, 1000000, 2500000, 5000000],
};
const stepsFor = (currency: string) => RAISE_STEPS[currency] || RAISE_STEPS.USD;

function PriceTile({ s, featured }: { s: Schedule; featured?: boolean }) {
  return (
    <Link
      to={`/fees/${scheduleSlug(s.countryCode)}`}
      className={`group relative block overflow-hidden rounded-2xl p-5 transition hover:-translate-y-0.5 ${featured ? 'text-white' : 'bg-white/95 text-[#3b0a22]'}`}
      style={
        featured
          ? { background: 'linear-gradient(145deg, rgba(255,255,255,0.18), rgba(255,255,255,0.06))', border: '1px solid rgba(255,255,255,0.35)', backdropFilter: 'blur(8px)' }
          : { boxShadow: '0 18px 40px rgba(26,0,15,0.25)' }
      }
    >
      <div className="flex items-center justify-between gap-2">
        <span className={`text-[11px] font-semibold uppercase tracking-[0.16em] ${featured ? 'text-white/75' : 'text-[#d6006e]'}`}>
          {s.countryCode === DEFAULT_CODE ? 'International' : s.countryName}
        </span>
        {!s.isActive && <StatusPill label="Inactive" tone="slate" />}
      </div>
      <p className="mt-3 text-[34px] font-extrabold leading-none tracking-tight">{s.ratingFee ? money(s.ratingFee, s.currency) : 'Free'}</p>
      <p className={`mt-1 text-xs ${featured ? 'text-white/70' : 'text-[#9a6b82]'}`}>Credit rating · one-time processing fee · {taxLabel(s)}</p>
      <div className={`my-4 h-px ${featured ? 'bg-white/20' : 'bg-[#f6dbe7]'}`} />
      <p className="flex items-baseline gap-1.5">
        <span className="text-2xl font-extrabold">{s.successFeePercent}%</span>
        <span className={`text-xs ${featured ? 'text-white/70' : 'text-[#9a6b82]'}`}>of the total funded amount</span>
      </p>
      {s.minSuccessFee > 0 && <p className={`mt-1 text-xs ${featured ? 'text-white/60' : 'text-[#a77d92]'}`}>Minimum {money(s.minSuccessFee, s.currency)}</p>}
      <span className={`mt-4 inline-flex text-xs font-semibold ${featured ? 'text-white' : 'text-[#d6006e]'} opacity-0 transition group-hover:opacity-100`}>Edit pricing →</span>
    </Link>
  );
}

function EarningsCalculator({ schedules }: { schedules: Schedule[] }) {
  const active = schedules.filter((s) => s.isActive);
  const [code, setCode] = useState(active[0]?.countryCode || '');
  const s = active.find((x) => x.countryCode === code) || active[0];
  const steps = stepsFor(s?.currency || 'USD');
  const [idx, setIdx] = useState(2);
  if (!s) return null;
  const raise = steps[Math.min(idx, steps.length - 1)];
  const success = Math.max((raise * s.successFeePercent) / 100, s.minSuccessFee / 100);
  const ratingGross = s.ratingFee / 100;
  const net = (fee: number) => (s.taxInclusive && s.taxPercent ? fee / (1 + s.taxPercent / 100) : fee);
  const rating = net(ratingGross);
  const successNet = net(success);
  const total = successNet + rating;

  return (
    <Card title="Earnings per company" subtitle="What Micus keeps (before tax) when a rated company closes its raise." className="h-full">
      <Select
        value={s.countryCode}
        onChange={setCode}
        options={active.map((x) => ({
          value: x.countryCode,
          label: x.countryCode === DEFAULT_CODE ? 'International (default)' : x.countryName,
          badge: x.currency,
          prefix: <CodeChip code={x.countryCode === DEFAULT_CODE ? '★' : x.countryCode} />,
        }))}
      />
      <div className="mt-4">
        <div className="flex items-baseline justify-between">
          <span className="text-xs font-semibold text-[#7a1f4a]">Funding raised</span>
          <span className="text-lg font-bold text-[#3b0a22]">{moneyMajor(raise, s.currency, { compact: true })}</span>
        </div>
        <input type="range" className="mz-range mt-2" min={0} max={steps.length - 1} step={1} value={Math.min(idx, steps.length - 1)} onChange={(e) => setIdx(Number(e.target.value))} aria-label="Funding raised" />
        <div className="mt-0.5 flex justify-between text-[10.5px] text-[#b99aaa]">
          <span>{moneyMajor(steps[0], s.currency, { compact: true })}</span>
          <span>{moneyMajor(steps[steps.length - 1], s.currency, { compact: true })}</span>
        </div>
      </div>
      <dl className="mt-4 space-y-2 text-[13px]">
        <div className="flex justify-between">
          <dt className="text-[#9a6b82]">Processing (rating) fee</dt>
          <dd className="font-semibold">{moneyMajor(rating, s.currency)}</dd>
        </div>
        <div className="flex justify-between">
          <dt className="text-[#9a6b82]">Success fee · {s.successFeePercent}%</dt>
          <dd className="font-semibold">{moneyMajor(successNet, s.currency)}</dd>
        </div>
        <div className="flex items-baseline justify-between rounded-lg bg-[#fdf2f8] px-3 py-2.5">
          <dt className="font-semibold text-[#7a1f4a]">Total revenue</dt>
          <dd className="text-xl font-extrabold text-[#d6006e]">{moneyMajor(total, s.currency)}</dd>
        </div>
        {s.taxPercent > 0 && <p className="text-[11px] text-[#a77d92]">Companies also pay {s.taxName} {s.taxPercent}% — collected for the tax authority, not revenue.</p>}
      </dl>
    </Card>
  );
}

export default function FeesPage() {
  const navigate = useNavigate();
  const { data, loading, error } = useApi<FeesResponse>('/admin/fees');
  const schedules = data?.schedules || [];
  const gw = GATEWAY[data?.gateway || 'sandbox'] || GATEWAY.sandbox;

  const tiles = useMemo(() => {
    const inr = schedules.find((s) => s.countryCode === 'IN');
    const def = schedules.find((s) => s.countryCode === DEFAULT_CODE);
    return [inr, def].filter(Boolean) as Schedule[];
  }, [schedules]);

  /** Which schedule (and so which tax) applies to each country Micus has companies in. */
  const coverage = useMemo(() => {
    const byCode = new Map(schedules.filter((s) => s.isActive).map((s) => [s.countryCode, s]));
    const fallback = byCode.get(DEFAULT_CODE) || null;
    return (data?.companyCountries || [])
      .map((c) => ({ ...c, own: byCode.get(c.countryCode) || null, applied: byCode.get(c.countryCode) || fallback }))
      .sort((a, b) => b.companies - a.companies);
  }, [schedules, data]);

  const hasDefault = schedules.some((s) => s.countryCode === DEFAULT_CODE);
  const untaxed = schedules.filter(taxMissing);

  return (
    <div>
      <PageHeader
        title="Fees"
        description="Country pricing and tax only — rating fees, success fees, tax rates and registration. Payment transactions and invoices live under Payments."
        actions={
          <Link to="/fees/new" className="mz-btn-primary">
            + Country pricing
          </Link>
        }
      />

      {error && <Notice tone="error" className="mb-4">{error}</Notice>}

      <section
        className="mz-fade-up relative overflow-hidden rounded-2xl p-5 sm:p-6"
        style={{ background: 'radial-gradient(circle at 85% 0%, rgba(255,79,162,0.45), transparent 45%), linear-gradient(135deg, #1a000f 0%, #5e002f 45%, #c4005e 100%)' }}
      >
        <div className="pointer-events-none absolute -bottom-16 -left-10 h-48 w-48 rounded-full bg-white/10 blur-2xl" />
        <div className="relative grid min-w-0 items-center gap-5 lg:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)]">
          <div className="min-w-0 text-white">
            <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-white/70">Launch pricing</p>
            <h2 className="mt-2 text-2xl font-extrabold leading-tight tracking-tight">Processing up front. Success fee when funding lands.</h2>
            <p className="mt-2 max-w-md text-[13px] leading-relaxed text-white/75">
              Companies pay a one-time rating fee to see their credit rating. The success fee is {tiles[0]?.successFeePercent ?? 100}% of the total amount funded through Micus partners. Tax is applied per country.
            </p>
            <div className="mt-4 flex flex-wrap items-center gap-2 text-xs text-white/80">
              <StatusPill label={gw.label} tone={gw.tone} />
              <span>{gw.hint}</span>
            </div>
          </div>
          <div className="grid min-w-0 gap-3 sm:grid-cols-2">
            {loading && !data ? (
              <div className="h-48 animate-pulse rounded-2xl bg-white/10 sm:col-span-2" />
            ) : tiles.length ? (
              tiles.map((s, i) => <PriceTile key={s.id} s={s} featured={i === 1} />)
            ) : (
              <Link to="/fees/new" className="rounded-2xl bg-white/95 p-5 text-sm font-semibold text-[#d6006e] sm:col-span-2">
                Set your first price →
              </Link>
            )}
          </div>
        </div>
      </section>

      {!loading && !hasDefault && (
        <Notice tone="warning" className="mt-4">
          No international default yet — companies in countries without their own pricing are not charged. <Link to="/fees/new?country=default" className="font-semibold underline">Add the default</Link>
        </Notice>
      )}
      {untaxed.length > 0 && (
        <Notice tone="warning" className="mt-4">
          Tax is not set for {untaxed.map((s) => (s.countryCode === DEFAULT_CODE ? 'the international default' : s.countryName)).join(', ')}. Set a rate, or 0% with the statutory note (e.g. reverse charge), so invoices are correct.
        </Notice>
      )}

      <div className="mt-4 grid min-w-0 gap-4 2xl:grid-cols-[minmax(0,1fr)_340px]">
        <Card title="Pricing & tax by country" subtitle="Click a row to edit. Inactive schedules fall back to the international default." padded={false} className="min-w-0">
          {loading && !data ? (
            <LoadingRows rows={3} />
          ) : schedules.length === 0 ? (
            <EmptyState title="No pricing yet" action={<Link to="/fees/new" className="mz-btn-primary">+ Country pricing</Link>} />
          ) : (
            <div className="min-w-0 overflow-x-auto">
              <table className="mz-table">
                <thead>
                  <tr>
                    <th>Country</th>
                    <th>Processing fee</th>
                    <th>Success fee</th>
                    <th>Tax</th>
                    <th>Tax registration</th>
                    <th>Updated</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {schedules.map((s) => (
                    <tr key={s.id} className="mz-row-link" onClick={() => navigate(`/fees/${scheduleSlug(s.countryCode)}`)}>
                      <td>
                        <div className="flex items-center gap-2">
                          <CodeChip code={s.countryCode === DEFAULT_CODE ? '★' : s.countryCode} />
                          <span className="font-semibold">{s.countryCode === DEFAULT_CODE ? 'International default' : s.countryName}</span>
                        </div>
                      </td>
                      <td className="whitespace-nowrap font-semibold">{s.ratingFee ? money(s.ratingFee, s.currency) : 'Free'}</td>
                      <td className="whitespace-nowrap">
                        {s.successFeePercent}%
                        {s.minSuccessFee > 0 && <span className="text-xs text-[#a77d92]"> · min {money(s.minSuccessFee, s.currency, { compact: true })}</span>}
                      </td>
                      <td className="whitespace-nowrap text-[13px]">
                        {taxMissing(s) ? <StatusPill label="Not set" tone="amber" /> : taxLabel(s)}
                        {s.taxNote && <p className="max-w-[220px] truncate text-[11px] text-[#a77d92]" title={s.taxNote}>{s.taxNote}</p>}
                      </td>
                      <td className="text-xs text-[#9a6b82]">
                        {s.taxNumber ? <span className="font-mono">{s.taxNumber}</span> : '—'}
                        {s.serviceCode && <p className="text-[11px]">Code {s.serviceCode}</p>}
                      </td>
                      <td className="whitespace-nowrap text-xs text-[#9a6b82]">
                        {date(s.updatedAt)}
                        {s.updatedBy ? ` · ${s.updatedBy.name || s.updatedBy.email}` : ''}
                      </td>
                      <td>
                        <StatusPill label={s.isActive ? 'Live' : 'Inactive'} tone={s.isActive ? 'green' : 'slate'} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
        {schedules.length > 0 && <EarningsCalculator schedules={schedules} />}
      </div>

      <Card className="mt-4 min-w-0" title="Tax coverage" subtitle="The pricing and tax that apply to every country Micus has companies in." padded={false}>
        {coverage.length === 0 ? (
          <EmptyState title="No companies yet" />
        ) : (
          <div className="min-w-0 overflow-x-auto">
            <table className="mz-table">
              <thead>
                <tr>
                  <th>Country</th>
                  <th>Companies</th>
                  <th>Charged under</th>
                  <th>Tax applied</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {coverage.map((c) => (
                  <tr key={c.countryCode}>
                    <td>
                      <div className="flex items-center gap-2">
                        <CodeChip code={c.countryCode} />
                        <span className="font-semibold">{countryName(c.countryCode)}</span>
                      </div>
                    </td>
                    <td className="text-[#9a6b82]">{c.companies}</td>
                    <td className="text-[13px]">
                      {c.own ? 'Own pricing' : c.applied ? <span className="text-[#b45309]">International default</span> : <span className="text-[#be123c]">No pricing — not charged</span>}
                    </td>
                    <td className="text-[13px]">{c.applied ? taxLabel(c.applied) : '—'}</td>
                    <td className="text-right">
                      <Link to={c.own ? `/fees/${c.countryCode}` : `/fees/new?country=${c.countryCode}`} className="whitespace-nowrap text-xs font-semibold text-[#d6006e] hover:underline">
                        {c.own ? 'Edit' : 'Set local pricing & tax'}
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
